'use client'

import { useState, useEffect, useCallback } from 'react'
import { useLocale } from 'next-intl'
import { Link, useRouter } from '@/i18n/navigation'
import { useTheme } from 'next-themes'
import FinancialChart from '@/components/FinancialChart'
import { fetchEuQuotes, fetchEuChartData, type EuQuote } from '@/services/europe'
import { euBrokers } from '@/data/indexData'
import BrokerRecommendations from '@/components/BrokerRecommendations'

interface Props { symbol: string }

const periods = [
  { key: '1G', tr: '1G', en: '1D' },
  { key: '1H', tr: '1H', en: '1W' },
  { key: '1A', tr: '1A', en: '1M' },
  { key: '1Y', tr: '1Y', en: '1Y' },
  { key: '5Y', tr: '5Y', en: '5Y' },
]

function fmtEur(val: number): string { return '\u20AC' + val.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',') }
function fmtVol(vol: number): string {
  if (vol >= 1_000_000_000) return (vol / 1_000_000_000).toFixed(2) + 'B'
  if (vol >= 1_000_000) return (vol / 1_000_000).toFixed(2) + 'M'
  if (vol >= 1_000) return (vol / 1_000).toFixed(1) + 'K'
  return vol.toLocaleString('en-US')
}
function fmtBigNum(val: number): string {
  if (val >= 1_000_000_000_000) return '\u20AC' + (val / 1_000_000_000_000).toFixed(2) + 'T'
  if (val >= 1_000_000_000) return '\u20AC' + (val / 1_000_000_000).toFixed(2) + 'B'
  if (val >= 1_000_000) return '\u20AC' + (val / 1_000_000).toFixed(2) + 'M'
  if (val >= 1_000) return '\u20AC' + (val / 1_000).toFixed(1) + 'K'
  return '\u20AC' + val.toLocaleString('en-US')
}

function Skeleton({ w, h = 16, className = '' }: { w: number; h?: number; className?: string }) {
  return <div className={`animate-pulse rounded ${className}`} style={{ width: w, height: h, backgroundColor: 'var(--border-color)' }} />
}

export default function EuStockDetailClient({ symbol }: Props) {
  const lang = useLocale()
  const router = useRouter()
  const { theme } = useTheme()
  const isDark = theme === 'dark'
  const code = symbol?.toUpperCase() || ''

  const [quote, setQuote] = useState<EuQuote | null>(null)
  const [chartData, setChartData] = useState<{ time: number; value: number }[]>([])
  const [period, setPeriod] = useState('1H')
  const [chartLoading, setChartLoading] = useState(true)
  const [chartUpdatedAt, setChartUpdatedAt] = useState<number>(0)

  const loadQuote = useCallback(async () => { if (!code) return; const data = await fetchEuQuotes([code]); const q = data[code]; if (q) setQuote(q) }, [code])
  useEffect(() => { loadQuote(); const iv = setInterval(loadQuote, 60_000); return () => clearInterval(iv) }, [loadQuote])
  useEffect(() => { if (!code) return; setChartLoading(true); fetchEuChartData(code, period).then(d => { setChartData(d); setChartUpdatedAt(Date.now()); setChartLoading(false) }) }, [code, period])

  if (!code) {
    return (<main className="max-w-7xl mx-auto py-5 px-4"><div className="text-center py-24"><h1 className="text-2xl font-bold mb-4" style={{ color: 'var(--text-main)' }}>{lang === 'en' ? 'Stock not found' : 'Hisse bulunamadı'}</h1><button onClick={() => router.push('/')} className="px-6 py-2.5 rounded-lg font-medium transition-colors" style={{ backgroundColor: 'var(--accent-color)', color: '#fff' }}>{lang === 'en' ? 'Back to Home' : 'Ana Sayfaya Dön'}</button></div></main>)
  }

  const isUp = quote ? quote.changePercent >= 0 : true
  const clr = isUp ? 'var(--chart-up)' : 'var(--chart-down)'

  return (
    <main className="max-w-7xl mx-auto py-5 px-4 sm:px-6 lg:px-4">
      <nav className="flex items-center gap-2 text-[13px] mb-6 overflow-hidden whitespace-nowrap" style={{ color: 'var(--text-muted)' }}>
        <Link href="/" className="hover:underline transition-colors" style={{ color: 'var(--accent-color)' }}>{lang === 'en' ? 'Home' : 'Anasayfa'}</Link>
        <span className="select-none">/</span>
        <span>{lang === 'en' ? 'European Stocks' : 'Avrupa Hisse Senetleri'}</span>
        <span className="select-none">/</span>
        <span className="font-semibold" style={{ color: 'var(--text-main)' }}>{code}</span>
      </nav>

      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-3 mb-6">
        <div>
          <h1 className="text-3xl font-extrabold tracking-tight m-0" style={{ color: 'var(--text-main)' }}>{code}</h1>
          <p className="text-sm mt-1 m-0" style={{ color: 'var(--text-muted)' }}>{quote?.name || (lang === 'en' ? 'European Exchange (Tradegate)' : 'Avrupa Borsası (Tradegate)')}</p>
        </div>
        {quote ? (
          <div className="sm:text-right shrink-0">
            <div className="text-3xl font-bold tabular-nums" style={{ color: clr }}>{fmtEur(quote.price)}</div>
            <div className="text-base font-semibold mt-0.5 tabular-nums" style={{ color: clr }}>
              {isUp ? '+' : ''}{fmtEur(quote.change)}&nbsp;({isUp ? '+' : ''}%{Math.abs(quote.changePercent).toFixed(2).replace('.', ',')}){isUp ? ' \u2191' : ' \u2193'}
            </div>
          </div>
        ) : (<div className="sm:text-right space-y-2"><Skeleton w={160} h={32} className="ml-auto" /><Skeleton w={120} h={20} className="ml-auto" /></div>)}
      </div>

      <div className="rounded-xl border overflow-hidden mb-6" style={{ backgroundColor: 'var(--bg-card)', borderColor: 'var(--border-color)' }}>
        <div className="p-4 sm:p-6">
          {chartLoading ? <div className="animate-pulse rounded-lg" style={{ height: 420, backgroundColor: 'var(--border-color)', opacity: 0.5 }} /> : <FinancialChart data={chartData} isDark={isDark} height={420} />}
        </div>
        <div className="flex items-center gap-1 px-4 sm:px-6 pb-3">
          {periods.map(p => (<button key={p.key} onClick={() => setPeriod(p.key)} className="px-4 py-2 rounded-lg text-sm font-medium transition-colors" style={{ backgroundColor: period === p.key ? 'var(--accent-color)' : 'transparent', color: period === p.key ? '#fff' : 'var(--text-muted)' }}>{lang === 'en' ? p.en : p.tr}</button>))}
        </div>
        <div className="px-4 sm:px-6 pb-4 text-[11px]" style={{ color: 'var(--text-muted)' }}>
          {lang === 'en' ? 'Last updated' : 'Son güncelleme'}: {chartUpdatedAt > 0 ? (() => { const tz = 'Europe/Istanbul'; const d = new Date(chartUpdatedAt); return d.toLocaleDateString(lang === 'en' ? 'en-US' : 'tr-TR', { timeZone: tz }) + ' ' + d.toLocaleTimeString(lang === 'en' ? 'en-US' : 'tr-TR', { hour: '2-digit', minute: '2-digit', timeZone: tz }) })() : '\u2014'}
        </div>
      </div>

      <BrokerRecommendations brokers={euBrokers} symbol={code} source="eu_stock_detail" limit={4} />

      {quote && (
        <div className="rounded-xl border overflow-hidden mb-6 p-4 sm:p-6" style={{ backgroundColor: 'var(--bg-card)', borderColor: 'var(--border-color)' }}>
          <h2 className="text-lg font-bold mb-4 m-0" style={{ color: 'var(--text-main)' }}>{lang === 'en' ? 'Key Stats' : 'Temel Bilgiler'}</h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-5">
            {[
              { label: lang === 'en' ? 'Price' : 'Fiyat', value: fmtEur(quote.price), color: 'var(--text-main)' },
              { label: lang === 'en' ? 'Bid' : 'Alış (Bid)', value: fmtEur(quote.bid), color: 'var(--chart-up)' },
              { label: lang === 'en' ? 'Ask' : 'Satış (Ask)', value: fmtEur(quote.ask), color: 'var(--chart-down)' },
              { label: lang === 'en' ? 'Day Low' : 'En Düşük', value: fmtEur(quote.dayLow), color: 'var(--chart-down)' },
              { label: lang === 'en' ? 'Day High' : 'En Yüksek', value: fmtEur(quote.dayHigh), color: 'var(--chart-up)' },
              { label: lang === 'en' ? 'Prev. Close' : 'Önceki Kapanış', value: fmtEur(quote.previousClose), color: 'var(--text-main)' },
              { label: lang === 'en' ? 'Volume' : 'Hacim', value: fmtVol(quote.volume), color: 'var(--text-main)' },
              { label: lang === 'en' ? 'Turnover' : 'İşlem Hacmi', value: fmtBigNum(quote.turnover), color: 'var(--text-main)' },
            ].map(s => (<div key={s.label}><div className="text-[11px] uppercase tracking-wider font-semibold mb-1" style={{ color: 'var(--text-muted)' }}>{s.label}</div><div className="text-xl font-bold tabular-nums" style={{ color: s.color }}>{s.value}</div></div>))}
          </div>
        </div>
      )}
    </main>
  )
}
